import { useState, useEffect, useCallback } from 'preact/hooks';
import Button from '../components/shared/Button.jsx';
import Card from '../components/shared/Card.jsx';
import ProductCard from '../components/ProductCard.jsx';
import ProductFormModal from '../components/ProductFormModal.jsx';
import ProductPlansModal from '../components/ProductPlansModal.jsx';
import ProductUsersModal from '../components/ProductUsersModal.jsx';
import TokenDisplayModal from '../components/TokenDisplayModal.jsx';

const REFRESH_MS = 10_000;

export default function ProductsView({ onChanged }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [connectionTarget, setConnectionTarget] = useState(null);
  const [plansTarget, setPlansTarget] = useState(null);
  const [usersTarget, setUsersTarget] = useState(null);
  const [tokenModal, setTokenModal] = useState(null);
  // Het net geregistreerde product, dat na het wegklikken van zijn token direct
  // naar het pakkettenscherm doorstroomt. Zie onSaved.
  const [pendingPlans, setPendingPlans] = useState(null);
  const [notice, setNotice] = useState(null);
  // Why: één tick per seconde zodat "3m geleden" meeloopt zonder dat we
  // daarvoor de hele lijst opnieuw ophalen.
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/products', { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      setProducts(data.products || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const listInterval = setInterval(refresh, REFRESH_MS);
    const tickInterval = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(listInterval); clearInterval(tickInterval); };
  }, [refresh]);

  function openCreate() { setConnectionTarget(null); setConnectionOpen(true); }
  function openConnection(product) { setConnectionTarget(product); setConnectionOpen(true); }

  function afterChange() {
    refresh();
    onChanged?.();
  }

  function onSaved(data) {
    afterChange();
    if (!data.token) return;
    setTokenModal({ token: data.token, productName: data.product?.name || 'product' });
    // Why: een net geregistreerd product heeft nog geen pakketten en is dus
    // nergens zichtbaar. Zonder deze doorstroom lijkt het registreren klaar,
    // terwijl de helft van het werk nog moet gebeuren.
    setPendingPlans(data.product || null);
  }

  function closeTokenModal() {
    setTokenModal(null);
    if (!pendingPlans) return;
    setPlansTarget(pendingPlans);
    setPendingPlans(null);
  }

  async function deleteProduct(product) {
    if (!confirm(`Product "${product.name}" verwijderen? Dit ongedaan maken kan niet.`)) return;
    const res = await fetch(`/api/products/${product.id}`, { method: 'DELETE', credentials: 'same-origin' });
    if (res.ok) return afterChange();
    const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
    setNotice(data.error === 'product_in_use'
      ? `"${product.name}" heeft nog lopende abonnementen — zeg die eerst op.`
      : `"${product.name}" verwijderen is mislukt.`);
  }

  async function togglePause(product) {
    const action = product.paused ? 'resume' : 'pause';
    const res = await fetch(`/api/products/${product.id}/${action}`, { method: 'POST', credentials: 'same-origin' });
    if (res.ok) refresh();
  }

  async function regenerateToken(product) {
    if (!confirm(`Token voor "${product.name}" opnieuw genereren? Het oude wordt direct ongeldig.`)) return;
    const res = await fetch(`/api/products/${product.id}/regenerate-token`, { method: 'POST', credentials: 'same-origin' });
    if (!res.ok) return;
    const data = await res.json();
    setTokenModal({ token: data.token, productName: data.product?.name || product.name });
    refresh();
  }

  async function syncUsers(product) {
    try {
      const res = await fetch(`/api/products/${product.id}/sync-users`, { method: 'POST', credentials: 'same-origin' });
      if (!res.ok) {
        setNotice('Sync kon niet starten — probeer het opnieuw.');
        return;
      }
      const data = await res.json();
      setNotice(data.result?.ok
        ? `${product.name}: ${data.result.count} gebruiker(s) opgehaald.`
        : `${product.name}: sync mislukt — ${data.result?.reason || 'onbekende reden'}`);
    } catch {
      setNotice(`${product.name}: netwerkfout tijdens sync.`);
    } finally {
      refresh();
    }
  }

  return (
    <div class="products-tab">
      <div class="dashboard-section-head">
        <h2>Producten</h2>
        <Button onClick={openCreate}>+ Product toevoegen</Button>
      </div>

      {notice && (
        <div class="inline-notice" role="status">
          {notice}
          <button type="button" class="inline-notice-close" onClick={() => setNotice(null)} aria-label="Melding sluiten">&times;</button>
        </div>
      )}

      {loading ? (
        <Card><div class="empty-state muted">Producten laden…</div></Card>
      ) : products.length === 0 ? (
        <Card>
          <div class="empty-state">
            <p>Nog geen producten geregistreerd.</p>
            <p class="muted">Voeg Vereniging4U toe om status en gebruikers te volgen.</p>
          </div>
        </Card>
      ) : (
        <div class="products-grid">
          {products.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onEditConnection={openConnection}
              onEditPlans={setPlansTarget}
              onDelete={deleteProduct}
              onTogglePause={togglePause}
              onRegenerateToken={regenerateToken}
              onSyncUsers={syncUsers}
              onShowUsers={setUsersTarget}
            />
          ))}
        </div>
      )}

      <ProductFormModal
        isOpen={connectionOpen}
        product={connectionTarget}
        onClose={() => setConnectionOpen(false)}
        onSaved={onSaved}
      />
      <ProductPlansModal
        isOpen={!!plansTarget}
        product={freshProduct(products, plansTarget)}
        onClose={() => setPlansTarget(null)}
        onSaved={afterChange}
      />
      <ProductUsersModal
        isOpen={!!usersTarget}
        product={usersTarget}
        onClose={() => setUsersTarget(null)}
      />
      <TokenDisplayModal
        isOpen={!!tokenModal}
        token={tokenModal?.token}
        productName={tokenModal?.productName}
        nextStep={!!pendingPlans}
        onClose={closeTokenModal}
      />
    </div>
  );
}

// Why: het pakketvenster kreeg de momentopname van het product toen het
// openging, waardoor een verwijderd of toegevoegd pakket pas na een herlaad
// zichtbaar werd. Na elke wijziging en elke ophaalronde tonen we de versie
// uit de verse lijst. Een net geregistreerd product staat daar mogelijk nog
// niet in; dan blijft de momentopname staan.
function freshProduct(products, target) {
  if (!target) return null;
  return products.find(p => p.id === target.id) ?? target;
}
